// noinspection SpellCheckingInspection

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';
import camelCase from 'lodash.camelcase';
import isEmpty from 'lodash.isempty';
import { Buffer } from 'buffer';
import { Div, Icon, Input } from '@pinpoint/design-kit';
import { add, diff, divide, dotDivide, dotMultiply, multiply, subtract, sum } from 'mathjs';
import adler32 from 'adler-32';

import usePlatformConfig from 'src/hooks/usePlatformConfig';
import CONSTANTS from 'src/store/constants';
import Button from 'src/components/button';
import { all, calcEqPersonsAdj, calcEqPremiumsAdj, formatNumberV2, formatPercent, isValidNumber } from 'src/functions';
import {
  calculateYearlyDroppedPolicies,
  calculateYearlyValues,
  correctMeanPremiumPerPolicy,
  createPoliciesInForce,
  simplifyTypeDetail,
} from 'src/functions/actuarial_calculator';
import { ModelProp, RoiCalcSeedProp, RoiCalcStateProp } from 'src/store/typings';

import { CellRegulatoryTag, CellTag } from '../../cells';
import YearImpact from './year_impact';
import Legend from './legend';
import YearImpactDetail from './year_impact_detail';

/** @type YearDetail */
const defaultYearInfo = {
  sumPolicies: 0,
  policies: new Array(10).fill(0),
  loss: new Array(10).fill(0),
  sumLoss: 0,
  sumPremium: 0,
  premium: new Array(10).fill(0),
  lr: new Array(10).fill(0),
  totalLR: 0,
};
/**
 *
 * @param {number[]} acquisitionDistribution
 * @param {number[]} nonRenewalDistribution
 * @param {number[][]} yearlyRates
 * @param {number} policiesInForce
 * @return boolean
 */
const isInputValid = ({ acquisitionDistribution, nonRenewalDistribution, yearlyRates, policiesInForce }) => {
  const policiesValid = isValidNumber(policiesInForce);
  const acqDValid = acquisitionDistribution.every(isValidNumber);
  const nrDValid = nonRenewalDistribution.every(isValidNumber);
  const yrValid = yearlyRates.flat().every(isValidNumber);
  return policiesValid && acqDValid && nrDValid && yrValid;
};

/**
 * @type {number[]}
 */
const DECILES = Array.from(Array(10), (e, i) => i + 1);

export default function ActuarialRoiCalculator({
  app: {
    profile: { tenant, email },
  },
  location: { pathname, search: queryParams },
  history,
  modelView: {
    exportRoiCalculatorReady,
    equalType,
    dataset,
    model: {
      name,
      id: modelId,
      dataModelType,
      framework,
      typeDetail,
      predictionType,
      modelData: { dataPayload, metadataPayload },
      payload,
    },
    roiCalculatorState: { yearlyRates, nonRenewalDistribution, acquisitionDistribution, policiesInForce },
  },
  resetRoiCalcStateField,
  updateRoiCalcState,
  initRoiCalcState,
  saveRoiCalcState,
  setReadyExport,
}) {
  const platformConfig = usePlatformConfig();
  const isLegacy = ['LIFT_VERSION_1_ACT', null, undefined].includes(payload?.version);
  const isPersonalAuto = typeDetail.includes('PERSONAL AUTO');
  const goodAcqD = sum(acquisitionDistribution) === 100;
  const goodNrD = sum(nonRenewalDistribution) === 100;

  const [shouldInit, setShouldInit] = useState(true);
  const [lastCalcUpdate, setLastCalcUpdate] = useState({});

  const liftScores = useMemo(() => {
    if (isLegacy) return dataPayload?.lift_scores ?? [];
    if (!isLegacy && equalType === null) return [];
    if (equalType === 'premiums')
      return dataPayload?.equal_premiums?.lift_scores ?? dataPayload?.equal_premium?.lift_scores;
    if (equalType === 'persons') return dataPayload?.equal_persons?.lift_scores;
    return dataPayload?.lift_scores ?? [];
  }, [dataPayload, isLegacy, equalType]);
  const seed = useMemo(() => dataset?.payload?.roi_calculator_seed ?? {}, [dataset]);
  const liftScore = useMemo(() => liftScores.find((info) => info?.p_lift?.length === 10), [liftScores]);
  const saveCalculator = useCallback(
    ({ input, version }) => {
      if (!isEqual(lastCalcUpdate, input) && isInputValid(input)) {
        saveRoiCalcState({
          thinkalikeModelId: modelId,
          state: { version, ...input },
          stateMetadata: { email, tenant },
        });
        setLastCalcUpdate(input);
      }
    },
    [lastCalcUpdate, modelId, email, tenant, saveRoiCalcState]
  );
  const saveCalcDebounced = useMemo(() => debounce(saveCalculator, 750), [saveCalculator]);
  const exportReady = useMemo(() => debounce(setReadyExport, 750), [setReadyExport]);
  const [scratch, setScratch] = useState({
    wP: {
      y1: defaultYearInfo,
      y2: defaultYearInfo,
      y3: defaultYearInfo,
    },
    ss: {
      y1: defaultYearInfo,
      y2: defaultYearInfo,
      y3: defaultYearInfo,
    },
    crnt: defaultYearInfo,
  });
  const [calculate, setCalculate] = useState(true);

  const processDistInput = useCallback(
    (evt) => {
      evt.preventDefault();
      if (exportRoiCalculatorReady) exportReady(false);
      const { target } = evt;
      const { id, value: trial } = target;
      /** @type {string} */
      const validOnly = trial.replace(/\D+/g, '');
      updateRoiCalcState(id, +validOnly);
      const position = `${+validOnly}`.length;
      setTimeout(() => {
        target.setSelectionRange(position, position);
      }, 25);
    },
    [exportReady, exportRoiCalculatorReady, updateRoiCalcState]
  );
  const processPercentageInput = useCallback(
    (evt) => {
      evt.preventDefault();
      if (exportRoiCalculatorReady) exportReady(false);
      const { target } = evt;
      const { id, value: trial } = target;
      const validOnly = +trial.replace(/\D+/g, '');
      if (![null, undefined].includes(validOnly)) updateRoiCalcState(id, validOnly > 100 ? 100 : validOnly);
      else updateRoiCalcState(id, 0);
      const position = `${validOnly}`.length;
      setTimeout(() => target.setSelectionRange(position, position), 25);
    },
    [exportReady, exportRoiCalculatorReady, updateRoiCalcState]
  );

  const processIntegerInput = useCallback(
    (evt) => {
      evt.preventDefault();
      if (exportRoiCalculatorReady) exportReady(false);
      const { target } = evt;
      const { value: trial } = target;
      const validOnly = +trial.replace(/\D+/g, '');
      updateRoiCalcState('policiesInForce', validOnly);
    },
    [exportReady, exportRoiCalculatorReady, updateRoiCalcState]
  );

  useEffect(() => {
    if (!liftScore?.p_pif_percentile) return;
    const mdl = {};
    /** @type {YearDetail} */
    const crnt = {};
    const cncl = {
      y1: {},
      y2: {},
      y3: {},
    };
    /**
     * @property {YearDetail} y1
     * @property {YearDetail} y2
     * @property {YearDetail} y3
     */
    const ss = {
      y1: {},
      y2: {},
      y3: {},
    };
    /**
     * @property {YearDetail} y1
     * @property {YearDetail} y2
     * @property {YearDetail} y3
     */
    const wP = {
      y1: {},
      y2: {},
      y3: {},
    };
    /** @type {math.Unit} */
    const newBDR = divide(acquisitionDistribution, 100);
    const [[y1nr, y1nb], [y2nr, y2nb], [y3nr, y3nb]] = divide(yearlyRates, 100);
    // adjusted values by pif percentile
    const { pinpointLosses, pinpointPremiums } =
      equalType === 'persons'
        ? calcEqPersonsAdj(liftScore?.p_pif_percentile ?? [], liftScore?.p_risk_cost ?? [], liftScore?.p_premium ?? [])
        : calcEqPremiumsAdj(
            liftScore?.p_pif_percentile ?? new Array(10).fill(0),
            liftScore?.p_risk_cost ?? [],
            liftScore?.p_premium ?? []
          );

    // Current and Model Data
    crnt.policies = createPoliciesInForce(policiesInForce, isPersonalAuto);
    crnt.sumPolicies = sum(crnt.policies);

    mdl.loss = pinpointLosses;
    mdl.sumLoss = sum(mdl.loss);
    mdl.premium = pinpointPremiums;
    mdl.sumPremium = sum(mdl.premium);
    mdl.avgPremium = correctMeanPremiumPerPolicy(liftScore?.avg_premium, seed?.avg_yearly_premium, isPersonalAuto);
    mdl.sumAvgPremium = sum(mdl.avgPremium);

    crnt.premium = dotMultiply(crnt.policies, mdl.avgPremium);
    crnt.sumPremium = sum(crnt.premium);
    crnt.lr = dotDivide(mdl.loss, mdl.premium);
    crnt.totalLR = mdl.sumLoss / mdl.sumPremium;

    // Year 1 - Steady State
    ss.y1 = calculateYearlyValues(
      multiply(crnt.policies, 1 + Math.abs(diff(yearlyRates[0])) / 100),
      crnt,
      mdl.avgPremium
    );

    // Year 2 - Steady State
    ss.y2 = calculateYearlyValues(
      multiply(ss.y1.policies, 1 + Math.abs(diff(yearlyRates[1])) / 100),
      ss.y1,
      mdl.avgPremium
    );

    // Year 3 - Steady State
    ss.y3 = calculateYearlyValues(
      multiply(ss.y2.policies, 1 + Math.abs(diff(yearlyRates[2])) / 100),
      ss.y2,
      mdl.avgPremium
    );

    // Year 1 - With Pinpoint
    cncl.y1 = calculateYearlyDroppedPolicies(y1nr, crnt, nonRenewalDistribution);
    wP.y1 = calculateYearlyValues(
      subtract(add(multiply(newBDR, y1nb, crnt.sumPolicies), crnt.policies), cncl.y1.finalDrop),
      crnt,
      mdl.avgPremium
    );

    // Year 2 - With Pinpoint
    cncl.y2 = calculateYearlyDroppedPolicies(y2nr, wP.y1, nonRenewalDistribution);
    wP.y2 = calculateYearlyValues(
      subtract(add(multiply(newBDR, y2nb, wP.y1.sumPolicies), wP.y1.policies), cncl.y2.finalDrop),
      wP.y1,
      mdl.avgPremium
    );

    // Year 3 - With Pinpoint
    cncl.y3 = calculateYearlyDroppedPolicies(y3nr, wP.y2, nonRenewalDistribution);
    wP.y3 = calculateYearlyValues(
      subtract(add(multiply(newBDR, y3nb, wP.y2.sumPolicies), wP.y2.policies), cncl.y3.finalDrop),
      wP.y2,
      mdl.avgPremium
    );

    if (calculate) {
      exportReady(true);
      setCalculate(false);
      setScratch({
        mdl,
        crnt,
        ss,
        wP,
      });
      saveCalcDebounced({
        version: '3.0.0',
        input: {
          policiesInForce,
          yearlyRates,
          acquisitionDistribution,
          nonRenewalDistribution,
        },
      });
    }
  }, [
    acquisitionDistribution,
    calculate,
    equalType,
    exportReady,
    isPersonalAuto,
    liftScore?.avg_premium,
    liftScore?.p_pif_percentile,
    liftScore?.p_premium,
    liftScore?.p_risk_cost,
    nonRenewalDistribution,
    policiesInForce,
    saveCalcDebounced,
    seed?.avg_yearly_premium,
    setReadyExport,
    yearlyRates,
  ]);

  useEffect(() => {
    // NOTE: update the calculators initial state from queryParams
    const query = new URLSearchParams(queryParams);
    let strB64 = query.get('state') || 'e30K';
    strB64 = strB64.replace(/-/g, '+').replace(/_/g, '/');
    if (strB64 !== 'e30K') {
      /** @type {Buffer} */
      const bufferB64 = Buffer.from(strB64, 'base64');
      const stateStr = bufferB64.toString('utf8');
      const paramState = JSON.parse(stateStr);
      const camelizedState = {};
      Object.keys(paramState).forEach((key) => {
        camelizedState[camelCase(key)] = paramState[key];
      });
      initRoiCalcState({ ...JSON.parse(CONSTANTS.TAL_MODEL.ROI_CALC_DEFAULTS), ...camelizedState });
      query.set('state', 'e30K');
      history.push({ pathname, search: query.toString() });
      setShouldInit(false);
    } else if (!isEmpty(seed) && strB64 === 'e30K' && typeDetail && shouldInit) {
      initRoiCalcState({
        ...JSON.parse(CONSTANTS.TAL_MODEL.ROI_CALC_DEFAULTS),
        acquisitionRate: seed?.new_business_rate ?? 0,
        carrierNonRenewalRate: seed?.non_renewal_rate ?? 0,
        policiesInForce: seed?.policies_in_force ?? 0,
        averagePremium: seed?.avg_yearly_premium ?? 0,
        riskCost: seed?.avg_yearly_direct_loss_cost ?? 0,
      });
      setShouldInit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, seed, initRoiCalcState, typeDetail, queryParams, shouldInit]);

  return (
    <Div
      d="grid"
      h="100%"
      w="100%"
      maxH="100%"
      maxW="100%"
      minH="100%"
      minW="100%"
      p="1rem"
      gridTplC="repeat(16, 1fr)"
      gridTplR="repeat(15, 1fr)"
      gridGap="calc(1rem/4)"
      textSize="caption"
      textColor="shade4D"
    >
      <Legend orient="v" />
      <Div
        d="grid"
        gridArea="1 / 2 / 5 / 5"
        gridTplC="repeat(5, 1fr)"
        gridTplR="2rem repeat(3, 1fr) .25rem repeat(2, 2rem) repeat(2, 1fr)"
        gridGap="calc(1rem/16)"
        data-debug="inputs"
      >
        <Div
          d="flex"
          align="center"
          justify="center"
          textWeight="500"
          gridArea="1 / 1 / 1 / -1"
          bg="shadeEF"
          rounded="sm"
        >
          INPUTS
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-end"
          p={{ r: '.5rem' }}
          textWeight="500"
          gridArea="2 / 1 / 3 / 3"
          bg="rioGrande"
          rounded="sm"
          whiteSpace="nowrap"
        >
          Line of Business:
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-start"
          p={{ l: '.5rem' }}
          textWeight={undefined}
          gridArea="2 / 3 / 3 / -1"
          bg="marigoldPale"
          rounded="sm"
        >
          {simplifyTypeDetail(typeDetail)}
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-end"
          p={{ r: '.5rem' }}
          textWeight="500"
          gridArea="3 / 1 / 4 / 3"
          bg="rioGrande"
          rounded="sm"
          whiteSpace="nowrap"
        >
          Model Selection:
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-start"
          p={{ l: '.5rem' }}
          textWeight={undefined}
          gridArea="3 / 3 / 4 / -1"
          bg="marigoldPale"
          rounded="sm"
        >
          {CONSTANTS.TAL_MODEL.TAG_SMP_DESC?.[dataModelType] ?? 'Actuarial'}
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-end"
          p={{ r: '.5rem' }}
          textWeight="500"
          gridArea="4 / 1 / 5 / 3"
          bg="rioGrande"
          rounded="sm"
          whiteSpace="nowrap"
        >
          Policies in Force:
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-start"
          p={{ l: '.5rem', r: '.25rem' }}
          textWeight={undefined}
          gridArea="4 / 3 / 5 / -1"
          bg="marigoldPale"
          rounded="sm"
        >
          <Input
            textColor="shade4D"
            textAlign="right"
            type="text"
            id="pif-input"
            placeholder={formatNumberV2(seed?.policies_in_force, true, true)}
            name="pif-input"
            onChange={processIntegerInput}
            value={policiesInForce ? formatNumberV2(policiesInForce, true, true) : ''}
            borderColor="shadeD9"
            border="1px solid"
            maxH="1rem"
            h="1rem"
            m="0"
            p={{ x: '.25rem' }}
            rounded="sm"
            textSize="small"
          />
        </Div>
        <Div
          d="flex"
          align="center"
          justify="center"
          textWeight="500"
          gridArea="6 / 1 / 7 / -1"
          bg="shadeEF"
          rounded="sm"
        >
          PINPOINT UW SELECTION INPUTS
        </Div>
        <Div
          d="flex"
          align="center"
          justify="center"
          textWeight="500"
          gridArea="7 / 1 / 8 / 3"
          bg="shadeEF"
          rounded="sm"
        />
        <Div
          d="flex"
          align="center"
          justify="center"
          textWeight="500"
          gridArea="7 / 3 / 8 / 4"
          bg="shadeEF"
          rounded="sm"
        >
          YEAR 1
        </Div>
        <Div
          d="flex"
          align="center"
          justify="center"
          textWeight="500"
          gridArea="7 / 4 / 8 / 5"
          bg="shadeEF"
          rounded="sm"
        >
          YEAR 2
        </Div>
        <Div
          d="flex"
          align="center"
          justify="center"
          textWeight="500"
          gridArea="7 / 5 / 8 / 6"
          bg="shadeEF"
          rounded="sm"
        >
          YEAR 3
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-end"
          p={{ r: '.5rem' }}
          textWeight="500"
          gridArea="8 / 1 / 9 / 3"
          bg="rioGrande"
          rounded="sm"
          whiteSpace="nowrap"
        >
          Non-Renewal Rate*:
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-start"
          textWeight={undefined}
          gridArea="8 / 3 / 9 / 4"
          bg="marigoldPale"
          rounded="sm"
          p={{ x: '.25rem' }}
        >
          <Input
            textColor="shade4D"
            textAlign="right"
            type="text"
            id="yearlyRates.0.0"
            placeholder={`${yearlyRates[0][0]}%`}
            name="yearlyRates.0.0"
            maxLength={4}
            onChange={processPercentageInput}
            onClick={(evt) => {
              const { target } = evt;
              const { value } = target;
              target.setSelectionRange(value.length - 1, value.length - 1);
            }}
            value={`${yearlyRates[0][0]}%`}
            borderColor="shadeD9"
            border="1px solid"
            maxH="1rem"
            h="1rem"
            m="0"
            p={{ x: '.25rem' }}
            rounded="sm"
            textSize="small"
          />
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-start"
          p={{ x: '.25rem' }}
          textWeight={undefined}
          gridArea="8 / 4 / 9 / 5"
          bg="marigoldPale"
          rounded="sm"
        >
          <Input
            textColor="shade4D"
            textAlign="right"
            type="text"
            id="yearlyRates.1.0"
            placeholder={`${yearlyRates[1][0]}%`}
            name="yearlyRates.1.0"
            maxLength={4}
            onChange={processPercentageInput}
            onClick={(evt) => {
              const { target } = evt;
              const { value } = target;
              target.setSelectionRange(value.length - 1, value.length - 1);
            }}
            value={`${yearlyRates[1][0]}%`}
            borderColor="shadeD9"
            border="1px solid"
            maxH="1rem"
            h="1rem"
            m="0"
            p={{ x: '.25rem' }}
            rounded="sm"
            textSize="small"
          />
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-start"
          p={{ x: '.25rem' }}
          textWeight={undefined}
          gridArea="8 / 5 / 9 / 6"
          bg="marigoldPale"
          rounded="sm"
        >
          <Input
            textColor="shade4D"
            textAlign="right"
            type="text"
            id="yearlyRates.2.0"
            placeholder={`${yearlyRates[2][0]}%`}
            name="yearlyRates.2.0"
            maxLength={4}
            onChange={processPercentageInput}
            onClick={(evt) => {
              const { target } = evt;
              const { value } = target;
              target.setSelectionRange(value.length - 1, value.length - 1);
            }}
            value={`${yearlyRates[2][0]}%`}
            borderColor="shadeD9"
            border="1px solid"
            maxH="1rem"
            h="1rem"
            m="0"
            p={{ x: '.25rem' }}
            rounded="sm"
            textSize="small"
          />
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-end"
          p={{ r: '.5rem' }}
          textWeight="500"
          gridArea="9 / 1 / 10 / 3"
          bg="rioGrande"
          rounded="sm"
          whiteSpace="nowrap"
        >
          New Business Rate**:
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-start"
          textWeight={undefined}
          gridArea="9 / 3 / 10 / 4"
          bg="marigoldPale"
          rounded="sm"
          p={{ x: '.25rem' }}
        >
          <Input
            textColor="shade4D"
            textAlign="right"
            type="text"
            id="yearlyRates.0.1"
            placeholder={`${yearlyRates[0][1]}%`}
            name="yearlyRates.0.1"
            maxLength={4}
            onChange={processPercentageInput}
            onClick={(evt) => {
              const { target } = evt;
              const { value } = target;
              target.setSelectionRange(value.length - 1, value.length - 1);
            }}
            value={`${yearlyRates[0][1]}%`}
            borderColor="shadeD9"
            border="1px solid"
            maxH="1rem"
            h="1rem"
            m="0"
            p={{ x: '.25rem' }}
            rounded="sm"
            textSize="small"
          />
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-start"
          p={{ x: '.25rem' }}
          textWeight={undefined}
          gridArea="9 / 4 / 10 / 5"
          bg="marigoldPale"
          rounded="sm"
        >
          <Input
            textColor="shade4D"
            textAlign="right"
            type="text"
            id="yearlyRates.1.1"
            placeholder={`${yearlyRates[1][1]}%`}
            name="yearlyRates.1.1"
            maxLength={4}
            onChange={processPercentageInput}
            onClick={(evt) => {
              const { target } = evt;
              const { value } = target;
              target.setSelectionRange(value.length - 1, value.length - 1);
            }}
            value={`${yearlyRates[1][1]}%`}
            borderColor="shadeD9"
            border="1px solid"
            maxH="1rem"
            h="1rem"
            m="0"
            p={{ x: '.25rem' }}
            rounded="sm"
            textSize="small"
          />
        </Div>
        <Div
          d="flex"
          align="center"
          justify="flex-start"
          p={{ x: '.25rem' }}
          textWeight={undefined}
          gridArea="9 / 5 / 10 / 6"
          bg="marigoldPale"
          rounded="sm"
        >
          <Input
            textColor="shade4D"
            textAlign="right"
            type="text"
            id="yearlyRates.2.1"
            placeholder={`${yearlyRates[2][1]}%`}
            name="y3-nbr"
            maxLength={4}
            onChange={processPercentageInput}
            onClick={(evt) => {
              const { target } = evt;
              const { value } = target;
              target.setSelectionRange(value.length - 1, value.length - 1);
            }}
            value={`${yearlyRates[2][1]}%`}
            borderColor="shadeD9"
            border="1px solid"
            maxH="1rem"
            h="1rem"
            m="0"
            p={{ x: '.25rem' }}
            rounded="sm"
            textSize="small"
          />
        </Div>
      </Div>
      <Div
        gridArea="1 / 5 / 5 / -1"
        d="grid"
        gridTplC="repeat(2, 1fr) calc(calc(1rem/4) - 2px) repeat(3, 1fr) calc(calc(1rem/4) - 2px) repeat(3, 1fr) calc(calc(1rem/4) - 2px) repeat(3, 1fr) calc(calc(1rem/4) - 2px) 1fr"
        gridTplR="repeat(2, 2rem) 3rem repeat(2, 2rem) 1fr"
        gridGap="calc(1rem/16)"
        textWeight="700"
        data-debug="summary"
      >
        <Div bg="shadeEF" rounded="sm" gridArea="1 / 1 / 4 / 3" />
        <Div
          d="flex"
          gridArea="4 / 1 / 5 / 3"
          align="center"
          justify="flex-end"
          p={{ r: '.5rem' }}
          bg="shadeEF"
          rounded="sm"
        >
          Loss Ratio
        </Div>
        <Div
          d="flex"
          gridArea="5 / 1 / 6 / 3"
          align="center"
          justify="flex-end"
          p={{ r: '.5rem' }}
          bg="shadeEF"
          rounded="sm"
        >
          Premium - (Loss + LAE)
        </Div>
        <Div d="flex" align="center" justify="center" gridArea="1 / 4 / 2 / -1" bg="shadeEF" rounded="sm">
          PINPOINT IMPACT SUMMARY PINPOINT IMPACT SUMMARY&emsp;
          {CellTag({
            value: {
              dataModelType,
              typeDetail,
              predictionType,
              isRemediated: metadataPayload?.fairness ?? false,
              isLegacy: platformConfig.isLegacyPlatform(metadataPayload?.refresh?.platform ?? 1),
            },
            bg: 'white',
          })}
          {framework && CellRegulatoryTag({ value: { framework }, bg: 'white', m: { l: '.25rem' } })}
          &emsp;
          {name?.split?.(': ')?.[1]}
        </Div>
        <YearImpact year={1} crnt={scratch.crnt} ss={scratch.ss.y1} wP={scratch.wP.y1} />
        <YearImpact year={2} crnt={scratch.crnt} ss={scratch.ss.y2} wP={scratch.wP.y2} />
        <YearImpact year={3} crnt={scratch.crnt} ss={scratch.ss.y3} wP={scratch.wP.y3} />
        <Div
          d="flex"
          align="center"
          justify="center"
          gridArea="2 / 16 / 3 / -1"
          gridTplC="repeat(3, 1fr)"
          gridTplR="2rem 3rem repeat(2, 2rem) 1fr"
          gridGap="calc(1rem/16)"
          bg="shadeEF"
          rounded="sm"
        >
          TOTAL
        </Div>
        <Div
          d="flex"
          p={{ r: '.75rem' }}
          align="center"
          justify="right"
          gridArea="3 / 16 / 4 / -1"
          bg="tealLight"
          rounded="sm"
          textColor="white"
          textAlign="right"
        >
          Expected
          <br />
          Impact
        </Div>
        <Div
          d="flex"
          p={{ r: '.75rem' }}
          align="center"
          justify="right"
          gridArea="4 / 16 / 5 / -1"
          bg="greenDarkPale2"
          rounded="sm"
          textAlign="right"
          whiteSpace="nowrap"
        >
          {formatPercent(
            Number.isFinite(scratch.wP.y3.totalLR - scratch.crnt.totalLR)
              ? (scratch.wP.y3.totalLR - scratch.crnt.totalLR) * 100
              : 0,
            false
          )}
        </Div>
        <Div
          d="flex"
          p={{ r: '.75rem' }}
          align="center"
          justify="right"
          gridArea="5 / 16 / 6 / -1"
          bg="greenDarkPale2"
          rounded="sm"
          textAlign="right"
          whiteSpace="nowrap"
        >
          $
          {formatNumberV2(
            ['y1', 'y2', 'y3'].reduce((acc, key) => acc + scratch.wP[key].sumNet - scratch.ss[key].sumNet, 0),
            true,
            true
          ) ?? 0}
        </Div>
      </Div>
      <Div
        d="grid"
        gridArea="5 / 1 / 14 / -1"
        gridTplR="repeat(2, 2rem) repeat(2, 2fr) repeat(12, .8fr)"
        gridTplC="repeat(3, 1fr) repeat(2, 1.2fr) repeat(5, 1fr) calc(calc(1rem/4) - 2px) repeat(5, 1fr) calc(calc(1rem/4) - 2px) repeat(5, 1fr)"
        gridGap="calc(1rem/16)"
        data-debug="table"
      >
        <Div
          d="flex"
          align="center"
          justify="center"
          textWeight="700"
          gridArea="1 / 6 / 1 / -1"
          bg="shadeEF"
          rounded="sm"
        >
          EXPECTED IMPACTS
        </Div>
        <Div gridArea="3 / 1 / 4 / 2" bg="tealLight" rounded="sm" />
        <Div gridArea="3 / 2 / 4 / 3" bg="skyBlue" rounded="sm" />
        <Div gridArea="3 / 3 / 4 / 4" bg="skyBlue" rounded="sm" />
        <Div
          d="flex"
          gridArea="3 / 4 / 4 / 6"
          bg="rioGrande"
          rounded="sm"
          align="center"
          justify="center"
          textAlign="center"
          textWeight="700"
          whiteSpace="nowrap"
        >
          UW Distribution Selections***
        </Div>
        <Div
          d="flex"
          align="center"
          justify="right"
          textAlign="right"
          textWeight="700"
          p={{ r: '.75rem' }}
          gridArea="4 / 1 / 5 / 2"
          bg="powderGreenPale2"
          rounded="sm"
        >
          Prediction Decile
        </Div>
        {DECILES.map((val, idx) => (
          <Div
            key={`decile-${val}`}
            d="flex"
            align="center"
            justify="right"
            textAlign="right"
            p={{ x: '.75rem' }}
            gridArea={`${5 + idx} / 1 / ${6 + idx} / 2`}
            bg="powderGreenPale2"
            rounded="sm"
          >
            {idx + 1}
          </Div>
        ))}
        <Div
          d="flex"
          align="center"
          justify="right"
          textAlign="right"
          p={{ r: '.75rem' }}
          textWeight="700"
          gridArea="15 / 1 / 16 / 2"
          bg="greenDarkPale3"
          rounded="sm"
        >
          TOTAL
        </Div>
        <Div
          d="flex"
          align="center"
          justify="right"
          textAlign="right"
          textWeight="700"
          p={{ r: '.75rem' }}
          gridArea="4 / 2 / 5 / 3"
          bg="skyBluePale2"
          rounded="sm"
        >
          Current Yearly Policies
        </Div>
        {scratch.crnt.policies
          .map((val) => formatNumberV2(val, true))
          .map((val, idx) => (
            <Div
              key={`current-pif-${adler32.str(val, idx)}`}
              d="flex"
              align="center"
              justify="right"
              textAlign="right"
              p={{ x: '.75rem' }}
              gridArea={`${5 + idx} / 2 / ${6 + idx} / 3`}
              bg="skyBluePale2"
              rounded="sm"
            >
              {val}
            </Div>
          ))}
        <Div
          data-test="current-pif-sum"
          d="flex"
          align="center"
          justify="right"
          textAlign="right"
          p={{ x: '.75rem' }}
          gridArea="15 / 2 / 16 / span 1"
          bg="skyBluePale3"
          rounded="sm"
          whiteSpace="nowrap"
        >
          {formatNumberV2(scratch.crnt.sumPolicies, true, true)}
        </Div>
        <Div
          d="flex"
          align="center"
          justify="right"
          textAlign="right"
          textWeight="700"
          p={{ r: '.75rem' }}
          gridArea="4 / 3 / 5 / 4"
          bg="skyBluePale2"
          rounded="sm"
        >
          Current
          <br />
          Loss Ratio
        </Div>
        {multiply(scratch.crnt.lr, 100)
          .map((val) => formatPercent(val, false))
          ?.map((val, idx) => (
            <Div
              key={`current-loss-ratio-${val}`}
              d="flex"
              align="center"
              justify="right"
              textAlign="right"
              p={{ x: '.75rem' }}
              gridArea={`${5 + idx} / 3 / ${6 + idx} / 4`}
              bg="skyBluePale2"
              rounded="sm"
            >
              {val}
            </Div>
          ))}
        <Div
          data-test="current-loss-ratio-sum"
          d="flex"
          align="center"
          justify="right"
          textAlign="right"
          p={{ x: '.75rem' }}
          gridArea="15 / 3 / 16 / span 1"
          bg="skyBluePale3"
          rounded="sm"
          whiteSpace="nowrap"
        >
          {formatPercent(scratch.crnt.totalLR * 100, false)}
        </Div>
        <Div
          d="flex"
          align="center"
          justify="right"
          textAlign="right"
          textWeight="700"
          p={{ r: '.75rem' }}
          gridArea="4 / 4 / 5 / 5"
          bg="marigoldPale"
          rounded="sm"
        >
          Pinpoint
          <br />
          New Business Distribution
        </Div>
        {acquisitionDistribution.map((val, idx) => (
          <Div
            key={`ppt-nb-dist-${adler32.str(idx.toString(), idx)}`}
            d="flex"
            align="center"
            justify="right"
            textAlign="right"
            p={{ x: '.5rem' }}
            gridArea={`${5 + idx} / 4 / ${6 + idx} / 5`}
            bg="marigoldPale"
            rounded="sm"
          >
            <Input
              textColor="shade4D"
              textAlign="right"
              type="text"
              id={`acquisitionDistribution.${idx}`}
              placeholder={`${val}%`}
              name={`acquisitionDistribution.${idx}`}
              maxLength={4}
              onChange={processDistInput}
              onClick={(evt) => {
                const { target } = evt;
                const { value } = target;
                target.setSelectionRange(value.length - 1, value.length - 1);
              }}
              value={`${val}%`}
              borderColor="shadeD9"
              border="1px solid"
              maxH="1rem"
              h="1rem"
              m="0"
              p={{ x: '.25rem' }}
              rounded="sm"
              textSize="small"
            />
          </Div>
        ))}
        <Div
          data-test="ppt-nb-dist-sum"
          d="flex"
          align="center"
          justify="right"
          textAlign="right"
          p={{ x: '.5rem' }}
          textWeight="700"
          gridArea="15 / 4 / 16 / 5"
          bg="marigoldDarkPale2"
          rounded="sm"
          textColor={goodAcqD ? 'shade4D' : 'danger800'}
          whiteSpace="nowrap"
        >
          <Button
            m={{ r: '0.25rem' }}
            p={{ x: '0.25rem' }}
            h="1rem"
            bg="rioGrande"
            textSize="tiny"
            textWeight="700"
            onClick={() => resetRoiCalcStateField('acquisitionDistribution')}
          >
            <Icon name="Reload" size="0.625rem" viewBox="0 0 12 11.75" color="white" m={{ r: '.25rem' }} />
            DEFAULT
          </Button>
          {sum(acquisitionDistribution)}%
        </Div>
        <Div
          d="flex"
          align="center"
          justify="right"
          textAlign="right"
          textWeight="700"
          p={{ r: '.75rem' }}
          gridArea="4 / 5 / 5 / 6"
          bg="marigoldPale"
          rounded="sm"
        >
          Pinpoint
          <br />
          Non-Renewal Distribution
        </Div>
        {nonRenewalDistribution.map((val, idx) => (
          <Div
            key={`ppt-nr-dist-${adler32.str(idx.toString(), idx)}`}
            d="flex"
            align="center"
            justify="right"
            textAlign="right"
            p={{ x: '.5rem' }}
            gridArea={`${5 + idx} / 5 / ${6 + idx} / 6`}
            bg="marigoldPale"
            rounded="sm"
          >
            <Input
              textColor="shade4D"
              textAlign="right"
              type="text"
              id={`nonRenewalDistribution.${idx}`}
              placeholder={`${val}%`}
              name={`nonRenewalDistribution.${idx}`}
              maxLength={4}
              onChange={processDistInput}
              onClick={(evt) => {
                const { target } = evt;
                const { value } = target;
                target.setSelectionRange(value.length - 1, value.length - 1);
              }}
              value={`${val}%`}
              borderColor="shadeD9"
              border="1px solid"
              maxH="1rem"
              h="1rem"
              m="0"
              p={{ x: '.25rem' }}
              rounded="sm"
              textSize="small"
            />
          </Div>
        ))}
        <Div
          data-test="ppt-nr-dist-sum"
          d="flex"
          align="center"
          justify="right"
          textAlign="right"
          p={{ x: '.5rem' }}
          gridArea="15 / 5 / 16 / 6"
          bg="marigoldDarkPale2"
          rounded="sm"
          textWeight="700"
          textColor={goodNrD ? 'shade4D' : 'danger800'}
          whiteSpace="nowrap"
        >
          <Button
            m={{ r: '0.25rem' }}
            p={{ x: '0.25rem' }}
            h="1rem"
            bg="rioGrande"
            textSize="tiny"
            textWeight="700"
            onClick={() => resetRoiCalcStateField('nonRenewalDistribution')}
          >
            <Icon name="Reload" size="0.625rem" viewBox="0 0 12 11.75" color="white" m={{ r: '.25rem' }} />
            DEFAULT
          </Button>
          {sum(nonRenewalDistribution)}%
        </Div>
        <YearImpactDetail year={1} ss={scratch.ss.y1} wP={scratch.wP.y1} />
        <YearImpactDetail year={2} ss={scratch.ss.y2} wP={scratch.wP.y2} />
        <YearImpactDetail year={3} ss={scratch.ss.y3} wP={scratch.wP.y3} />
        <Div
          data-test="calc-btn-div"
          d="flex"
          align="center"
          justify="center"
          p={{ x: '.75rem', t: '.25rem' }}
          gridArea="16 / 4 / 17 / 6"
        >
          <Button
            onClick={() => setCalculate(true)}
            buttonStyle="primary"
            h="1.5rem"
            textSize="small"
            disabled={!all([goodAcqD, goodNrD])}
          >
            <Icon name="Roi" size="0.625rem" viewBox="0 0 16 16" color="white" m={{ r: '.25rem' }} />
            CALCULATE
          </Button>
        </Div>
      </Div>
      <Div gridArea="14 / 1 / -1 / -1" p={{ t: '1rem' }} textColor="shade4D" textWeight="500" textSize="small">
        <br />
        <Div textColor="brand500" textSize="body" textWeight="700">
          ABOUT
        </Div>
        <Div p={{ t: '.125rem' }}>
          This Impact Calculator shows how implementing a Pinpoint model via non-renewal and new-business strategies
          impacts the distribution of policies and profitability over time.
        </Div>
        <Div p={{ t: '.125rem' }}>
          * The Non-Renewal Rate represents the percent of in-force policies that the company is willing to triage with
          the selected Pinpoint Model.
        </Div>
        <Div p={{ t: '.125rem' }}>
          ** The New Business Rate represents the number of new-business policies the company is willing to target based
          on the Pinpoint Model, as a percent of the selected policies in force.
        </Div>
        <Div p={{ t: '.125rem' }}>
          *** The UW Distribution table allows you to make reasonable selections that allocate the non-renewed and
          new-business policies to their respective deciles based on the UW Selection Inputs above.
        </Div>
      </Div>
    </Div>
  );
}

ActuarialRoiCalculator.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.shape({ search: PropTypes.string.isRequired, pathname: PropTypes.string.isRequired }).isRequired,
  app: PropTypes.shape({
    profile: PropTypes.shape({ tenant: PropTypes.string.isRequired, email: PropTypes.string.isRequired }).isRequired,
  }).isRequired,
  // disableInput: PropTypes.bool.isRequired,
  modelView: PropTypes.shape({
    exportRoiCalculatorReady: PropTypes.bool.isRequired,
    equalType: PropTypes.string.isRequired,
    disableInput: PropTypes.bool.isRequired,
    dataset: PropTypes.shape({
      payload: PropTypes.shape({
        roi_calculator_seed: RoiCalcSeedProp.isRequired,
      }),
    }),
    model: ModelProp.isRequired,
    roiCalculatorState: RoiCalcStateProp.isRequired,
  }).isRequired,
  initRoiCalcState: PropTypes.func.isRequired,
  resetRoiCalcStateField: PropTypes.func.isRequired,
  saveRoiCalcState: PropTypes.func.isRequired,
  updateRoiCalcState: PropTypes.func.isRequired,
  setReadyExport: PropTypes.func.isRequired,
};
